import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { FilterEvent, FilterProperties, Filters } from '../../../sidebar/filters/filters.model';
import { combineLatest, Subject } from 'rxjs';
import { _, parseToDate } from '../../../shared/utils';
import { compareAsc } from 'date-fns/esm';
import { SidebarPanelComponent } from '../../../sidebar/sidebar-panel/sidebar-panel.component';
import { AppMessageService } from '../../../shared/app-message/app-message.service';
import { LandingPageTile } from '../../../shared/tiles/home-tile.model';
import { StateService } from '../../../shared/state/state.service';
import { PlusCode, Station } from '../common/station';
import { VRGroupService } from '../vrgroup.service';
import { find, remove, uniqBy } from 'lodash-es';
import { takeUntil } from 'rxjs/operators';
import { RunningTrain, ScheduleTrain } from '../common/train';
import { AlarmService } from '../../../alarm/alarm.service';
import { AlarmMessage } from '../../../alarm/alarm-message';
import { ConflictAlarmTypes, TechnicalAlarmTypes } from '../../../alarm/alarm-definitions/interfaces';
import { InfoActionService } from '../../../element-utils/element-actions/info-action/info-action.service';
import { ITrainListComponent } from '../utils/train-list.type';
import { getScheduleTrainWithStatus } from '../utils/train.utils';

@Component({
  selector: 'qm-arriving-trains',
  templateUrl: './arriving-trains.component.html',
  styleUrls: ['./arriving-trains.component.scss']
})
export class ArrivingTrainsComponent extends SidebarPanelComponent implements OnInit, OnDestroy, ITrainListComponent {
  public readonly columnWidths = {
    train: '4.7em',
    time: '6.7em',
    route: '6.5em'
  };
  public listLimit = 40;
  public alarmOverlaySelectedTrain: RunningTrain;
  public trainFilter: Map<string, FilterProperties[]> = new Map();
  public trainTypeFilters = [
    {
      id: 1,
      name: _('Trains'),
      type: 'dropdown-multiple',
      filter_values: [
        {
          name: _('Commuter'),
          value: 'COMMUTER',
          boolean: true
        },
        {
          name: _('Long-distance'),
          value: 'LONGDISTANCE',
          boolean: true
        },
        {
          name: _('Freight train'),
          value: 'FREIGHT',
          boolean: true
        },
        {
          name: _('Locomotive'),
          value: 'LOCOMOTIVE',
          boolean: false
        },
        {
          name: _('Other'),
          value: 'SHUNTING',
          boolean: false
        },
      ]
    }
  ];
  public typeAheadStations: Filters = {
    id: 1,
    name: 'Stations',
    type: 'type-ahead',
    filter_values: [],
    noFiltersShowsAll: true
  };
  public activeStations: FilterProperties[] = [];
  /**
   * Subscription manager
   * @type {Subject<any>}
   */
  private ngUnsubscribe: Subject<boolean> = new Subject();
  /**
   * All received trains
   */
  public allTrains: (RunningTrain | ScheduleTrain)[] = [];
  public displayedTrains: (RunningTrain | ScheduleTrain)[] = [];

  constructor(protected appMessageService: AppMessageService,
    private changeDetector: ChangeDetectorRef,
    private vrGroupService: VRGroupService,
    private state: StateService,
    private alarmService: AlarmService,
    private infoActionService: InfoActionService) {
    super(appMessageService);
    this.componentName = ArrivingTrainsComponent.getComponentName();
  }

  public static initTile(tile: LandingPageTile) {
    tile.description = _('Arriving trains for next hour');
    tile.icon = 'far fa-arrow-to-right';
    tile.title = _('Train arrivals');
    tile.panel = ArrivingTrainsComponent.getComponentName();
  }

  private static getComponentName(): string {
    return 'ArrivingTrains';
  }

  ngOnInit() {
    const temp = this.state.getPartOfState('arrivingTrains_filters');
    if (temp) {
      this.trainTypeFilters = temp[0];
      this.activeStations = temp[1];
    }
    for (const s of this.trainTypeFilters) {
      this.trainFilter.set(s.name, s.filter_values);
    }
    combineLatest([
      this.vrGroupService.runningTrainData$,
      this.vrGroupService.scheduledTrainData$
    ])
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(([runningTrains, scheduledTrains]) => {
        this.allTrains = [
          ...this.handleRunningTrainUpdate(runningTrains),
          ...this.handleTrainScheduleUpdate(scheduledTrains)
        ];
        this.displayedTrains = this.prepareTrainsToBeDisplayed(this.allTrains);
        if (this.allTrains.length > 0) {
          this.loading = false;
        }
        this.changeDetector.markForCheck();
      });
    this.getStations();
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  private stationToFilterProperties(station: Station): FilterProperties {
    return {
      name: station.stationName + ' (' + station.stationShortCode + ')',
      value: station.stationShortCode,
      boolean: this.checkIfActive({ value: station.stationShortCode })
    };
  }

  private async getStations() {
    if (this.typeAheadStations.filter_values.length < 1) {
      const stations: Station[] = await this.vrGroupService.getStationList();
      const plusCodes = await this.vrGroupService.getPlusCodes();
      this.typeAheadStations.filter_values = stations
        .map(station => this.stationToFilterProperties(station))
        .concat(
          Array.from(plusCodes.values()).map(plus => ({
            name: `${plus.name} (${plus.code})`,
            value: plus.code,
            boolean: this.checkIfActive({ value: plus.code }),
          }))
        );
    }
    this.changeDetector.markForCheck();
  }

  public getActiveTrainStoppedAlarms = (train: RunningTrain) => {
    return train.alarms.filter(a => a.metadata.active && a.metadata.type === 'vrTrainStopped');
  }

  public getActiveConflictAlarms = (train: RunningTrain) => {
    return this.filterActiveAlarms(this.filterVrTrainConflictAlarms(train));
  }

  public getActiveTechnicalAlarms = (train: RunningTrain) => {
    return this.filterActiveAlarms(this.filterVrTrainTechnicalAlarms(train));
  }

  public hasActiveConflictAlarms = (train: RunningTrain) => {
    return this.getActiveConflictAlarms(train).length > 0;
  }

  public hasActiveTechnicalAlarms = (train: RunningTrain) => {
    return this.getActiveTechnicalAlarms(train).length > 0;
  }

  // public getConflictOrTechnicalAlarms(train: RunningTrain) {
  //   return [].concat(this.getActiveConflictAlarmsForTrain(train), this.getActiveTechnicalAlarmsForTrain(train));
  // }

  public hasActiveConflictOrTechnicalAlarms = (train: RunningTrain) => {
    return this.hasActiveConflictAlarms(train) || this.hasActiveTechnicalAlarms(train);
  }

  public filterActiveAlarms(alarms: AlarmMessage[]) {
    return alarms.filter(alarm => {
      return alarm.metadata.active === true;
    });
  }

  public filterVrTrainConflictAlarms(train: RunningTrain) {
    return train.alarms.filter(alarm => {
      return ConflictAlarmTypes.indexOf(alarm.metadata.type) !== -1;
    });
  }

  public filterVrTrainTechnicalAlarms(train: RunningTrain) {
    return train.alarms.filter(alarm => {
      return TechnicalAlarmTypes.indexOf(alarm.metadata.type) !== -1;
    });
  }

  public getTranslatedAlarm = (alarm: AlarmMessage) => {
    return this.alarmService.getTranslatedAlarm(alarm.metadata, false);
  }

  public async filtersChanged($event: FilterEvent): Promise<void> {
    if ($event) {
      const plusCodes = await this.vrGroupService.getPlusCodes();
      this.activeStations = uniqBy(
        $event.typeAheadResults.filter_values
          .flatMap(props => {
            // discard non-selected options
            if (!props.boolean) {
              return [];
            }

            const value = '' + props.value;
            // expand plus codes into individual stations
            if (value.endsWith('+')) {
              return plusCodes.get(value)
                .stations
                .map(station => this.stationToFilterProperties(station));
            }

            return [props];
          }),
        props => props.name,
      );
      this.typeAheadStations = {
        ...$event.typeAheadResults,
        filter_values: $event.typeAheadResults.filter_values.map(props => ({
          ...props,
          boolean: this.checkIfActive(props),
        }))
      };
      for (const s of $event.customFilters) {
        this.trainFilter.set(s.name, s.filter_values);
      }
      this.displayedTrains = this.prepareTrainsToBeDisplayed(this.allTrains);
      this.saveFilterState([$event.customFilters, this.activeStations]);
      this.changeDetector.markForCheck();
    }
  }

  public selectAlarmOverlayTrain(train: RunningTrain) {
    this.alarmOverlaySelectedTrain = train;
  }

  public removeFromActives(s) {
    this.activeStations = remove(this.activeStations, (o) => {
      return o !== s;
    });
    this.typeAheadStations.filter_values.forEach((o) => {
      if (s.value === o.value) {
        o.boolean = false;
      }
    });
    this.saveFilterState([this.trainTypeFilters, this.activeStations]);

    this.displayedTrains = this.prepareTrainsToBeDisplayed(this.allTrains);
    this.changeDetector.markForCheck();
    this.changeDetector.detectChanges();
  }

  private saveFilterState(filters) {
    this.state.updateState('arrivingTrains_filters', filters);
  }

  /**
   * Is train visible on list?
   * @param filterType
   * @param train
   * @returns {any}
   */
  public trainVisible(filterType: string, train: RunningTrain | ScheduleTrain): boolean {
    for (const filter of this.trainFilter.get(filterType)) {
      if (filter.value === train.type) {
        if (this.activeStations.length > 0 && filter.boolean) {
          return !!find(this.activeStations, (o) => {
            return o.value === train.arrStation ? true : false;
          });
        } else {
          return filter.boolean;
        }
      }
    }
  }

  /**
   * Return row styling for conflicts with other trains.
   * @param train - current train
   * @returns {string} -  class name for current row
   */
  public getRowClass(train: RunningTrain): string {
    return 'text-' + train.status;
  }

  /**
   * Apply filters and sort trains
   */
  public prepareTrainsToBeDisplayed(trains: (RunningTrain | ScheduleTrain)[]): (RunningTrain | ScheduleTrain)[] {
    return trains
      .filter(train => this.trainVisible('Trains', train))
      .sort((a: RunningTrain, b: RunningTrain) => compareAsc(new Date(a.arrTime).getTime(), new Date(b.arrTime).getTime()))
      // .sort((a: RunningTrain, b: RunningTrain) => compareAsc(a.arrTime, b.arrTime))
      .sort(train => train.flagged ? -1 : 0);
  }

  private handleRunningTrainUpdate(trains: RunningTrain[]): RunningTrain[] {
    return trains
      .filter(train => !!train.category)
      .map(train => ({
        arrTime: parseToDate(train.arrTime as any),
        depTime: parseToDate(train.depTime as any),
        createdAt: parseToDate(train.createdAt as any),
        ...train
      }));
  }

  private handleTrainScheduleUpdate(trains: ScheduleTrain[]) {
    return trains
      .filter(train => this.trainVisible('Trains', train))
      .map(train => getScheduleTrainWithStatus(train))
      .sort((a: ScheduleTrain, b: ScheduleTrain) => compareAsc(a.depTime, b.depTime))
      .sort(train => train.flagged ? -1 : 0);
  }

  private checkIfActive(testedStation: FilterProperties): boolean {
    // plus-codes are never selected; they get expanded into individual stations and that would cause conflicts
    const value = '' + testedStation.value;
    if (value.endsWith('+')) {
      return false;
    }
    return !!find(this.activeStations, activeStation => activeStation.value === testedStation.value);
  }

  public async openTrainInfo(train: RunningTrain) {
    const el = await this.vrGroupService.getTrainMapElement(train.tripNumber, train.tripDate);
    this.infoActionService.openDetails(el.layer, el);
  }

  public async openCargoInfo(train: RunningTrain) {
    const el = await this.vrGroupService.getTrainMapElement(train.tripNumber, train.tripDate);
    this.infoActionService.openDetails(el.layer, el, 'cargomanifest');
  }

  public onLoadMoreBtnClicked() {
    this.listLimit += 30;
  }
}
